import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import ContactForm from '../components/ContactForm';

const Terms = () => {
  return (
    <div className="terms-container">
      <div className="terms-wrapper">
        <Link to="/" className="back-link">
          <Button variant="ghost" className="back-button">
            <ArrowLeft className="back-icon" />
            Back to Home
          </Button>
        </Link>

        <div className="terms-content">
          <h1 className="terms-title">Yappr Terms of Service</h1>
          <p className="effective-date">Effective Date: December 11, 2025</p>

          <p className="intro-text">
            Welcome to Yappr! These Terms of Service ("Terms") govern your access to and use of the Yappr mobile application and related services (collectively, the "Service"), operated by Yappr Inc. ("we," "us," or "our"). By downloading, accessing, or using the Service, you agree to be bound by these Terms. If you do not agree, you may not use the Service.
          </p>

          <section className="terms-section">
            <h2>1. Eligibility</h2>
            <p>
              You must be at least 13 years old to use the Service (or older if required by law in your jurisdiction). If you are under 18, you represent that you have obtained consent from a parent or legal guardian. Accounts created by individuals under 13 will be terminated immediately upon discovery.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Account Registration and Security</h2>
            <p>
              To use certain features, you must create an account by providing accurate information, including your name, email address, and location data. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. Notify us immediately of any unauthorized access. We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Content and "Yaps"</h2>
            <p>
              Yappr is a location-based social media app where users can create and share posts called "Yaps," which may include text, photos, or videos. All Yaps are automatically deleted after 5 days, except for those related to events or restaurant pictures, which are retained indefinitely to allow for searching and discovery.
            </p>
            
            <h3>User Content Ownership</h3>
            <p>
              You retain ownership of the content you post ("User Content"). By posting, you grant us a worldwide, non-exclusive, royalty-free, sublicensable license to use, reproduce, modify, distribute, and display your User Content in connection with providing the Service, including for promotional purposes.
            </p>

            <h3>Content Guidelines</h3>
            <p>You agree not to post User Content that:</p>
            <ul>
              <li>Breaks the law.</li>
              <li>Proposes breaking the law.</li>
              <li>Glorifies, promotes, or threatens violence.</li>
              <li>Involves doxxing (e.g., revealing the current location of an individual without consent).</li>
              <li>Contains pornography.</li>
              <li>Includes links to cryptocurrency.</li>
              <li>Includes links to OnlyFans or similar platforms.</li>
              <li>Contains pornography (repeated for emphasis: explicit sexual content is strictly prohibited).</li>
              <li>Involves entrapment (e.g., attempts to deceive or provoke others into illegal actions, including but not limited to interactions with law enforcement).</li>
              <li>Infringes on copyrights, trademarks, or other intellectual property rights.</li>
            </ul>

            <p>Additionally, you agree not to:</p>
            <ul>
              <li>Post spam, scams, or unsolicited commercial content.</li>
              <li>Impersonate others or create fake accounts.</li>
              <li>Use automated tools (e.g., bots) to interact with the Service without our permission.</li>
              <li>Collect or harvest user data without consent.</li>
              <li>Interfere with the Service's functionality, such as by introducing viruses or malware.</li>
            </ul>

            <p>
              We may remove any User Content that violates these guidelines or for any other reason, at our sole discretion. Repeated violations may result in account suspension or termination.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Location-Based Features</h2>
            <p>
              Yappr relies on your device's location services to enable features like discovering nearby Yaps. By using the Service, you consent to the collection and use of your location data as described in our Privacy Policy. You can control location sharing through your device settings, but disabling it may limit functionality.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. Intellectual Property</h2>
            <p>
              The Service, including its design, trademarks (e.g., "Yappr" and "Yaps"), and software, is owned by us or our licensors. You may not copy, modify, or reverse-engineer any part of the Service without our written consent.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Third-Party Links and Services</h2>
            <p>
              The Service may contain links to third-party websites or services. We are not responsible for their content, privacy practices, or availability. Your interactions with them are at your own risk.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, without notice, for violations of these Terms or for any other reason. Upon termination, your license to use the Service ends, but sections that by nature survive (e.g., ownership, disclaimers) remain in effect.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Disclaimers and Limitation of Liability</h2>
            <p>
              The Service is provided "as is" without warranties of any kind. We disclaim all warranties, express or implied, including fitness for a particular purpose or non-infringement. We are not liable for any indirect, incidental, or consequential damages arising from your use of the Service. Our total liability shall not exceed $100 or the amount you paid us in the last 12 months, whichever is greater.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Indemnification</h2>
            <p>
              You agree to indemnify and hold us harmless from any claims, losses, or damages arising from your use of the Service, your User Content, or your violation of these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the State of Wyoming, USA, without regard to conflict of laws principles. Any disputes shall be resolved through binding arbitration in Sheridan, Wyoming, under the rules of the American Arbitration Association, except for small claims court matters.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Changes to Terms</h2>
            <p>
              We may update these Terms at any time. Continued use of the Service after changes constitutes acceptance. We will notify you of material changes via the app or email.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Miscellaneous</h2>
            <p>
              These Terms constitute the entire agreement between you and us. If any provision is unenforceable, the rest remain in effect. No waiver of any term is a waiver of any other.
            </p>
          </section>

          <section className="terms-section">
            <p className="contact-info">
              If you have questions, contact us at <a href="mailto:support@umenuhub.com">support@umenuhub.com</a>.
            </p>
          </section>

          <ContactForm />
        </div>
      </div>
    </div>
  );
};

export default Terms;